// Service to detect and cache the client's public IP address
class PublicIpService {
  constructor() {
    this.IP_CACHE_KEY = 'client_public_ip';
    this.cachedIp = null;
    this.initPromise = null;
  }

  // Initialize and fetch the public IP from reliable public IP APIs
  async fetchPublicIp() {
    // 1. Check memory cache
    if (this.cachedIp) {
      return this.cachedIp;
    }

    // 2. Check sessionStorage
    try {
      const storedIp = sessionStorage.getItem(this.IP_CACHE_KEY);
      if (storedIp && storedIp.trim() !== '') {
        this.cachedIp = storedIp.trim();
        return this.cachedIp;
      }
    } catch (e) {
      // sessionStorage might be restricted
    }

    // If already fetching, reuse existing promise
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = (async () => {
      // Try primary API (ipify)
      try {
        const response = await fetch('https://api.ipify.org?format=json');
        if (response.ok) {
          const data = await response.json();
          if (data && data.ip) {
            this.setCachedIp(data.ip);
            return data.ip;
          }
        }
      } catch (err) {
        // Fallback to secondary API
      }

      try {
        const fallbackRes = await fetch('https://api64.ipify.org?format=json');
        if (fallbackRes.ok) {
          const data = await fallbackRes.json();
          if (data && data.ip) {
            this.setCachedIp(data.ip);
            return data.ip;
          }
        }
      } catch (err) {
        console.warn('⚠️ Could not resolve public IP address from client side');
      }

      return null;
    })();

    return this.initPromise;
  }

  setCachedIp(ip) {
    this.cachedIp = ip;
    try {
      sessionStorage.setItem(this.IP_CACHE_KEY, ip);
    } catch (e) {
      // Ignore
    }
  }

  getPublicIpHeader() {
    const ip = this.cachedIp || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(this.IP_CACHE_KEY) : null);
    return ip ? { 'X-Client-Public-IP': ip } : {};
  }
}

const publicIpService = new PublicIpService();
// Eagerly initiate public IP detection in background
if (typeof window !== 'undefined') {
  publicIpService.fetchPublicIp().catch(() => {});
}

export default publicIpService;
